import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  TextField,
  Stack,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  LocationOn,
  Phone,
  Email,
  WhatsApp,
  AccessTime,
  Send,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  YouTube,
  WhatsApp as WhatsAppIcon,
  ContactPhone as ContactIcon,
} from "@mui/icons-material";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import CallToAction from "../components/CallToAction";
import { useDispatch, useSelector } from "react-redux";
import { createContact, clearError } from "../redux/slices/adminContactSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageTitle from "../components/PageTitle";
import ContactBanner from "../assets/images/contact.avif";

const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  // Redux state
  const { loading, error } = useSelector((state) => state.adminContact);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    try {
      const result = await dispatch(createContact(formData)).unwrap();
      
      toast.success("Thank you! We will get back to you soon.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Reset form on success
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      
    } catch (err) {
      toast.error(err?.message || "Something went wrong. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Show error from Redux in toast
  useEffect(() => {
    if (error) {
      toast.error(error || "Failed to send message. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const contactInfo = [
    {
      icon: <LocationOn sx={{ fontSize: 30 }} />,
      title: "Head Office",
      details: [
        "Shiv Manglam 6, AB 23, Kalwar Rd,",
        "Govindpura, Jhotwara, Hathod,",
        "Jaipur, Rajasthan - 302013",
      ],
      color: "var(--primary)",
    },
    {
      icon: <LocationOn sx={{ fontSize: 30 }} />,
      title: "Branch Office",
      details: [
        "Tharhounda Showroom, Gawdi Mod,",
        "Neem Ka Thana, Rajasthan - 332713",
      ],
      color: "var(--primary)",
    },
    {
      icon: <Phone sx={{ fontSize: 30 }} />,
      title: "Phone",
      details: ["+91 6378788756"],
      color: "var(--primary-dark)",
    }, 
    {
      icon: <Email sx={{ fontSize: 30 }} />,
      title: "Email",
      details: ["restaurantwala24@gmail.com"],
      color: "var(--primary)",
    },
  ];

  const socialLinks = [
    { icon: <Instagram />, url: "https://www.instagram.com/restaurantwala.in?utm_source=qr&xtok=MjB1bjAyYXRoNXpu", color: "#E4405F" },
    // { icon: <LinkedIn />, url: "https://linkedin.com", color: "#0A66C2" },
    { icon: <WhatsAppIcon />, url: "https://wa.me/916378788756?text=Hello RestaurantWala%20Team%2C%20I%20would%20like%20to%20inquire%20about%20your%20services.", color: "#25D366" },
  ];
    

  return (
    <>
    <PageTitle 
    title="Contact RestaurantWala – Get Restaurant Software Support"
    description="Contact RestaurantWala for product information, support and assistance with our smart restaurant management system."
    keywords="contact RestaurantWala, restaurant software support, restaurant management support, restaurant POS support"
    />
      <Navbar />
      <PageBanner
        title="Get In Touch"
        subtitle="Have questions? We're here to help! Reach out to us anytime."
        chipLabel="CONTACT"
        bgImage={ContactBanner}
        // bgImage="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?q=80&w=2070&auto=format&fit=crop"
      />

      <Box sx={{ bgcolor: "#ffffff", py: { xs: 6, md: 10 } }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Contact Form & Info */}
          <Grid container spacing={4}>
            {/* Form */}
            <Grid item size={{ xs: 12, md: 7 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: "24px",
                  bgcolor: "var(--bg-light)",
                  border: "1px solid var(--border)",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.3rem",
                    color: "var(--text)",
                    mb: 3,
                  }}
                >
                  Send us a Message
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            bgcolor: "#ffffff",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            bgcolor: "#ffffff",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={loading}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            bgcolor: "#ffffff",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            bgcolor: "#ffffff",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            bgcolor: "#ffffff",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                        disabled={loading}
                        fullWidth
                        sx={{
                          bgcolor: "var(--primary)",
                          color: "#fff",
                          py: 1.5,
                          borderRadius: "12px",
                          fontWeight: 700,
                          boxShadow: "var(--shadow-orange)",
                          "&:hover": {
                            bgcolor: "var(--primary-dark)",
                            transform: "translateY(-3px)",
                            boxShadow: "var(--shadow-orange-hover)",
                          },
                          "&:disabled": {
                            bgcolor: "var(--primary)",
                            opacity: 0.7,
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        {loading ? "Sending..." : "Send Message"}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>

            {/* Contact Info */}
            <Grid item size={{ xs: 12, md: 5 }}>
              <Stack spacing={3}>
                {contactInfo.map((info, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      bgcolor: "#ffffff",
                      border: "1px solid var(--border)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(8px)",
                        borderColor: "var(--primary)",
                        boxShadow: "var(--shadow-orange)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "12px",
                          bgcolor: "var(--bg-primary-soft)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: info.color,
                          flexShrink: 0,
                        }}
                      >
                        {info.icon}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "var(--text)",
                            fontSize: "1rem",
                          }}
                        >
                          {info.title}
                        </Typography>
                        {info.details.map((detail, idx) => (
                          <Typography
                            key={idx}
                            sx={{
                              color: "var(--text-light)",
                              fontSize: "14px",
                            }}
                          >
                            {detail}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Paper>
                ))}

                {/* Social Links */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    bgcolor: "#ffffff",
                    border: "1px solid var(--border)",
                    textAlign: "center",
                    "&:hover": {
                      borderColor: "var(--primary)",
                      boxShadow: "var(--shadow-orange)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "var(--text)",
                      mb: 2,
                    }}
                  >
                    Connect With Us
                  </Typography>
                  <Box
                    sx={{ display: "flex", justifyContent: "center", gap: 1 }}
                  >
                    {socialLinks?.map((social, index) => (
                      <Button
                        key={index}
                        href={social.url}
                        target="_blank"
                        sx={{
                          minWidth: 40,
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: `${social.color}15`,
                          color: social.color,
                          "&:hover": {
                            bgcolor: social.color,
                            color: "#fff",
                            transform: "scale(1.1)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        {social.icon}
                      </Button>
                    ))}
                  </Box>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <CallToAction />
      <Footer />
    </>
  );
};

export default Contact;
